import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client';
import {
  Mutation,
  MutationSetAccessGrantsIsOfficeModeEnabledArgs,
  Query,
  VisitorGroup
} from 'graphql/generated/graphqlTypes';
import { UPDATE_ACCESS_GRANT_WITH_IS_OFFICE_MODE_ENABLED } from 'graphql/mutations';
import { GET_VISITOR_GROUPS_FOR_TENANT_WITH_IS_OFFICE_MODE_ENABLED } from 'graphql/queries';
import { useEffect } from 'react';
import {
  setIsOfficeModeEnabledAction,
  updateAccessGrantsArrayWithIsOfficeModeEnabled,
  updateIsOfficeModeEnabledAction
} from 'state/actions/isOfficeModeEnabled';
import { visitorGroupsWithIsOfficeModeEnabledVar } from 'state/vars';
import { BaseHookProps } from '../shared/types';

const useIsOfficeModeEnabled = ({ handleFetchError }: BaseHookProps) => {
  const visitorGroupsWithIsOfficeModeEnabled = useReactiveVar(visitorGroupsWithIsOfficeModeEnabledVar);

  const [getVisitorGroupsWithIsOfficeModeEnabled, { loading }] = useLazyQuery<Pick<Query, 'getVisitorGroupsForTenant'>>(
    GET_VISITOR_GROUPS_FOR_TENANT_WITH_IS_OFFICE_MODE_ENABLED,
    {
      onCompleted: (data) => {
        setIsOfficeModeEnabledAction(data.getVisitorGroupsForTenant);
      },
      onError: () => {
        handleFetchError('Error while fetching groups with officeMode');
      },
      fetchPolicy: 'network-only'
    }
  );

  const checkIsOfficeModeEnabledForVisitorGroup = (id: string) => {
    const visitorGroupById = visitorGroupsWithIsOfficeModeEnabled.find((group) => group.id === id);
    return visitorGroupById && visitorGroupById.accessGrants.some((grant) => grant.isOfficeModeEnabled);
  };

  const getAccessGrantWithIsOfficeModeEnabledForGroup = (group: VisitorGroup, isOfficeModeEnabledForGroup: boolean) =>
    group.accessGrants.find((grant) => grant.isOfficeModeEnabled === isOfficeModeEnabledForGroup);

  const [
    updateAccessGrantIsOfficeModeEnabledMutation,
    { loading: updateAccessGrantIsOfficeModeEnabledMutationLoading }
  ] = useMutation<Pick<Mutation, 'setAccessGrantsIsOfficeModeEnabled'>, MutationSetAccessGrantsIsOfficeModeEnabledArgs>(
    UPDATE_ACCESS_GRANT_WITH_IS_OFFICE_MODE_ENABLED,
    {
      onCompleted: (data) => {
        updateIsOfficeModeEnabledAction(data.setAccessGrantsIsOfficeModeEnabled.id);
      },
      onError: () => {
        handleFetchError('Error while updating Access Grant');
      }
    }
  );

  useEffect(() => {
    getVisitorGroupsWithIsOfficeModeEnabled();
  }, [getVisitorGroupsWithIsOfficeModeEnabled]);

  const updateAccessGrantIsOfficeModeEnabled = async (groupId: string) => {
    const isOfficeModeEnabledForGroup = checkIsOfficeModeEnabledForVisitorGroup(groupId);
    const groupWithAccessGrant = visitorGroupsWithIsOfficeModeEnabled.find((group) => group.id === groupId);
    if (groupWithAccessGrant && isOfficeModeEnabledForGroup !== undefined) {
      const accessGrant = getAccessGrantWithIsOfficeModeEnabledForGroup(
        groupWithAccessGrant,
        isOfficeModeEnabledForGroup
      );
      const { data } = await updateAccessGrantIsOfficeModeEnabledMutation({
        variables: {
          accessGrantId: accessGrant!.id,
          isOfficeModeEnabled: !isOfficeModeEnabledForGroup
        },
        // Here Apollo doesn't update cache automatically, we have to do it manually
        update(cache, { data: updatedAccessGrantId }) {
          if (updatedAccessGrantId?.setAccessGrantsIsOfficeModeEnabled) {
            const updatedVisitorGroups = visitorGroupsWithIsOfficeModeEnabled.map((group) => ({
              ...group,
              accessGrants: updateAccessGrantsArrayWithIsOfficeModeEnabled(
                updatedAccessGrantId.setAccessGrantsIsOfficeModeEnabled.id,
                group.accessGrants
              )
            }));

            cache.writeQuery({
              query: GET_VISITOR_GROUPS_FOR_TENANT_WITH_IS_OFFICE_MODE_ENABLED,
              data: {
                getVisitorGroupsForTenant: updatedVisitorGroups
              }
            });
          }
        }
      });

      return data;
    }
    return null;
  };

  return {
    visitorGroupsWithIsOfficeModeEnabled,
    checkIsOfficeModeEnabledForVisitorGroup,
    updateAccessGrantIsOfficeModeEnabled,
    updateAccessGrantIsOfficeModeEnabledMutationLoading,
    loading
  };
};

export default useIsOfficeModeEnabled;
